import React from 'react';
import { HashRouter as Router, Routes, Route, BrowserRouter } from 'react-router-dom';
import BookingFlow from './components/BookingFlow';
import PaymentSuccess from './components/PaymentSuccess';
import SmartRedirect from './components/SmartRedirect';
import ErrorBoundary from './components/ErrorBoundary';
import { isSubdomainMode } from './utils/routing';
import './App.css';

/**
 * PaymentReturnHandler - Handles path-based routes for Stripe 3DS redirect
 *
 * Stripe redirects to path-based URLs like /payment-return?payment_intent=xxx
 * This component detects those paths and renders PaymentSuccess directly,
 * bypassing the HashRouter which would lose the query params.
 */
function PaymentReturnHandler() {
  // Check if we're on a payment return path (path-based, not hash-based)
  const path = window.location.pathname;
  const isPaymentReturn = path === '/payment-return' || path === '/payment-success';

  if (isPaymentReturn) {
    // Render PaymentSuccess directly with BrowserRouter for path-based routing
    return (
      <BrowserRouter>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
          <Routes>
            <Route path="/payment-return" element={<PaymentSuccess />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
          </Routes>
        </div>
      </BrowserRouter>
    );
  }

  // For all other paths, use the normal HashRouter app
  return <HashRouterApp />;
}

function HashRouterApp() {
  const useSubdomains = isSubdomainMode();

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <Routes>
          {/* Payment success - also available via hash route for backwards compatibility */}
          <Route path="/payment-success" element={<PaymentSuccess />} />

          {/* Subdomain mode: only wildcard route (no merchantId in path) */}
          {useSubdomains && (
            <>
              <Route path="/*" element={<BookingFlow />} />
              <Route path="/" element={<SmartRedirect to="/locations" />} />
            </>
          )}

          {/* Path-based mode: include merchantId in routes */}
          {!useSubdomains && (
            <>
              <Route path="/:merchantId/*" element={<BookingFlow />} />
              <Route path="/cyclecentral" element={<SmartRedirect to="/cyclecentral/locations" />} />
              <Route path="/rideshare" element={<SmartRedirect to="/rideshare/locations" />} />
              <Route path="/" element={<SmartRedirect to="/rideshare/locations" />} />
            </>
          )}
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <PaymentReturnHandler />
    </ErrorBoundary>
  );
}

export default App;